// Single source of truth for the CRF data model:
// the flat 99-column export schema, the input form sections, derived research
// variables and the analysis scenarios. No DOM access here.

import { hasValue, num, type Row } from "./util";

export type FieldType = "text" | "number" | "select" | "formula";

export interface FieldDef {
  id: string;
  label: string;
  hint: string;
  type: FieldType;
  required?: boolean;
  derived?: boolean;
  compute?: (row: Row) => string;
}

export interface Section {
  title: string;
  fields: FieldDef[];
}

export interface Scenario {
  id: string;
  title: string;
  mode: string;
  group: string;
  outcome: string;
  covariates: string[];
  reference?: string;
  filter?: (row: Row) => boolean;
  filterLabel?: string;
}

/** Flat database columns — the stable CSV/JSON export contract (103 columns).
 *  IVUS_Post_Performed and the follow-up stress-test fields are appended at the
 *  end (not inserted into their clinical sections) so the position of every
 *  pre-existing column stays fixed for downstream pipelines that key by column
 *  index rather than name. */
export const DB_COLUMNS: string[] = [
  "Patient_ID","Lesion_ID","Age","Sex","BMI","Hypertension","Smoking","DM","CKD","Polyvascular_Dis","Prior_MI","Prior_Stroke","Prior_Revasc","Multivessel_CAD","LM_Disease","Clinical_Presentation","LVEF","LDL_Base","SYNTAX_Score_Base","Ischemia_Test_Type","Ischemia_Value","Ischemia_Severe","Vessel","Lesion_Type","Lesion_Length_Base","RVD_Base","MLD_Base","Stenosis_Base","Tortuosity","Calcification_Angio","Bifurcation","Medina_Class","CTO_Present","CTO_Length","J_CTO_Score","IVUS_Performed","LA_Proximal","LA_Distal","LA_Ref_Average","MLA_Base","MLA_to_Ref_Ratio","Calc_Arc_Max","Calc_Length","Calc_Thickness","Calc_Nodule","Strategy_Planned","CTO_Approach","CTO_Tracking","Bifurcation_Strategy","Rota_Used","Rota_Burr_Max","HP_OPN_Used","HP_OPN_Max_Press","Scoring_Balloon_Used","Cutting_Balloon_Used","POBA_Max_Press","IVUS_Calc_Mod_Confirmed","Dissection_Post_Prep","DCB_Used","DCB_Type","DCB_Diameter","DCB_Length_Total","DCB_Inflation_Time","Stent_Planned_Used","Stent_Planned_Length","BailOut_Stent_Used","BailOut_Reason","BailOut_Stent_Length","Stent_Length_Total","Complication_Any","Slow_No_Reflow","Distal_Embolization","Thrombosis_Acute","Perforation","Spasm_Severe","Complication_Therapy","MLD_Post","Residual_Stenosis","TIMI_Flow_Post","MLA_Post","MLA_Post_Ratio","Dissection_Final_Angio","Dissection_IVUS","Procedure_Success","ECG_Ischemia_Post","Angina_Post","Unplanned_Angio","Unplanned_Finding","Repeat_PCI_InHosp","MI_InHosp","Death_InHosp","FollowUp_Months","FollowUp_Type","Symptom_Driven_Angio","MACE_FU","TLR_FU","MLD_FU","LLL_FU","Restenosis_Binary","IVUS_Post_Performed","Ischemia_Test_Type_FU","Ischemia_Value_FU","Ischemia_Severe_FU",
];

function f(id: string, label: string, hint: string, type: FieldType, options: Partial<FieldDef> = {}): FieldDef {
  return { id, label, hint, type, ...options };
}

function d(id: string, label: string, hint: string, type: FieldType, compute: (row: Row) => string): FieldDef {
  return { id, label, hint, type, derived: true, compute };
}

export const SECTIONS: Section[] = [
  {
    title: "Блок 1: клинико-анамнестические данные",
    fields: [
      f("Patient_ID", "Номер пациента", "Текст/код: А-001, Д-001", "text", { required: true }),
      f("Lesion_ID", "Код поражения", "Текст/код поражения: А-001-1", "text", { required: true }),
      f("Age", "Возраст", "Полные годы", "number"),
      f("Sex", "Пол пациента", "1 = Муж, 0 = Жен", "select"),
      f("BMI", "Индекс массы тела", "Число (кг/м2)", "number"),
      f("Hypertension", "Артериальная гипертензия", "1 = Да, 0 = Нет", "select"),
      f("Smoking", "Статус курения", "0 = Никогда, 1 = В прошлом, 2 = Активный", "select"),
      f("DM", "Сахарный диабет", "0 = Нет, 1 = ИНСД (табл), 2 = ИЗСД (инсулин)", "select"),
      f("CKD", "Хроническая болезнь почек", "СКФ (мл/мин/1.73 м2)", "number"),
      f("Polyvascular_Dis", "Атеросклероз других бассейнов", "1 = Да, 0 = Нет", "select"),
      f("Prior_MI", "Перенесенный ИМ", "1 = Да, 0 = Нет", "select"),
      f("Prior_Stroke", "Инсульт / ТИА в анамнезе", "1 = Да, 0 = Нет", "select"),
      f("Prior_Revasc", "История реваскуляризаций", "0=Нет, 1=ЧКВ, 2=АКШ, 3=Повт. ЧКВ, 4=ЧКВ+АКШ", "select"),
      f("Multivessel_CAD", "Многососудистое поражение", "1 = Да, 0 = Нет", "select"),
      f("LM_Disease", "Поражение ствола ЛКА", "1 = Да, 0 = Нет", "select"),
      f("Clinical_Presentation", "Клинический статус", "1 = ОКС, 0 = ХИБС", "select"),
      f("LVEF", "Фракция выброса (ЭХОКГ)", "Число (%)", "number"),
      f("LDL_Base", "Холестерин ЛНП", "Число (ммоль/л)", "number"),
      f("SYNTAX_Score_Base", "Исходный балл SYNTAX", "Число", "number"),
      f("Ischemia_Test_Type", "Тип теста на ишемию", "0=Не треб., 1=Стресс-ЭХО, 2=ОФЭКТ, 3=ВЭМ, 4=ФРК", "select"),
      f("Ischemia_Value", "Количественный результат теста", "Число (сегменты/%/ФРК)", "number"),
      f("Ischemia_Severe", "Клинически значимая ишемия", "1 = Да, 0 = Нет, 99 = N/A", "select"),
    ],
  },
  {
    title: "Блок 2: ангиографическая оценка до вмешательства",
    fields: [
      f("Vessel", "Целевой сосуд", "Текст (авто-канонизация: ПМЖВ/ПНА→ПМЖА, ДА→ДВ, АТК→ВТК, ОВ→ОА, ЗМЖА→ЗМЖВ)", "text"),
      f("Lesion_Type", "Характер поражения", "0 = De novo, 1 = ISR", "select"),
      f("Lesion_Length_Base", "Исходная длина (QCA)", "Число (мм)", "number"),
      f("RVD_Base", "Референсный диаметр (QCA)", "Число (мм)", "number"),
      f("MLD_Base", "Мин. диаметр просвета (QCA)", "Число (мм)", "number"),
      f("Stenosis_Base", "Степень стеноза (QCA)", "Целое число %, без знака %", "number"),
      f("Tortuosity", "Выраженность извитости", "0=Нет, 1=Легкая, 2=Умер., 3=Выраж.", "select"),
      f("Calcification_Angio", "Кальциноз по ангиографии", "0=Нет, 1=Слабый, 2=Умер., 3=Тяжелый", "select"),
      f("Bifurcation", "Бифуркационное поражение", "1 = Да, 0 = Нет", "select"),
      f("Medina_Class", "Класс Медина", "Текст: 1,1,1; 1,1,0; N/A", "text"),
      f("CTO_Present", "Наличие ХТО", "1 = Да, 0 = Нет", "select"),
      f("CTO_Length", "Протяженность ХТО", "Число (мм)", "number"),
      f("J_CTO_Score", "Балл по шкале J-CTO", "Число 0-5, −99 = N/A", "number"),
    ],
  },
  {
    title: "Блок 3: ВСУЗИ до вмешательства",
    fields: [
      f("IVUS_Performed", "Выполнялось ли ВСУЗИ ДО?", "1 = Да, 0 = Нет", "select"),
      f("LA_Proximal", "Площадь просвета: проксимально", "Число (мм2)", "number"),
      f("LA_Distal", "Площадь просвета: дистально", "Число (мм2)", "number"),
      f("LA_Ref_Average", "Средний референс по ВСУЗИ", "Авто-расчет: (LA proximal + LA distal) / 2", "formula"),
      f("MLA_Base", "Мин. площадь просвета (MLA)", "Число (мм2)", "number"),
      f("MLA_to_Ref_Ratio", "MLA Ratio", "Авто-расчет: MLA_Base / LA_Ref_Average", "formula"),
      f("Calc_Arc_Max", "Дуга кальциноза (максимум)", "Число (0-360 град)", "number"),
      f("Calc_Length", "Протяженность кальция", "Число (мм)", "number"),
      f("Calc_Thickness", "Макс. толщина кальция", "Число (мм)", "number"),
      f("Calc_Nodule", "Наличие узла (Nodule)", "1 = Да, 0 = Нет, 99 = N/A", "select"),
    ],
  },
  {
    title: "Блок 4: стратегия и ход процедуры",
    fields: [
      f("Strategy_Planned", "Запланированная стратегия", "1=DCB, 2=Rota, 3=Гибрид, 4=FMJ", "select"),
      f("CTO_Approach", "Доступ (при ХТО)", "0=Нет, 1=Анте, 2=Ретро, 99=N/A", "select"),
      f("CTO_Tracking", "Техника (при ХТО)", "0=Нет, 1=Внутрипросвет., 2=Субинтим., 99=N/A", "select"),
      f("Bifurcation_Strategy", "Стратегия (при бифуркации)", "0=Нет, 1=Стент+DCB, 2=DCB+DCB, 3=2 стента, 4=Prov, 99=N/A", "select"),
      f("Rota_Used", "Использовалась ли ротабляция?", "1 = Да, 0 = Нет", "select"),
      f("Rota_Burr_Max", "Максимальный диаметр бура", "Число (мм)", "number"),
      f("HP_OPN_Used", "Использовался ли OPN (HP)?", "1 = Да, 0 = Нет", "select"),
      f("HP_OPN_Max_Press", "Давление на OPN баллоне", "Число (атм)", "number"),
      f("Scoring_Balloon_Used", "Скоринг-баллон (AngioSculpt)", "1 = Да, 0 = Нет", "select"),
      f("Cutting_Balloon_Used", "Режущий баллон (Wolverine)", "1 = Да, 0 = Нет", "select"),
      f("POBA_Max_Press", "Макс. давление подготовки (NC)", "Число (атм)", "number"),
      f("IVUS_Calc_Mod_Confirmed", "Трещины кальция по ВСУЗИ?", "1 = Да, 0 = Нет, 99 = N/A", "select"),
      f("Dissection_Post_Prep", "Диссекция после подготовки", "0=Нет, 1=Safe (A-C), 2=Need treat (D-F)", "select"),
      f("DCB_Used", "Использовался ли DCB?", "1 = Да, 0 = Нет", "select"),
      f("DCB_Type", "Тип покрытия DCB", "1=Agent, 2=Selution, 99=N/A", "select"),
      f("DCB_Diameter", "Максимальный диаметр DCB", "Число (мм)", "number"),
      f("DCB_Length_Total", "Суммарная длина DCB", "Число (мм)", "number"),
      f("DCB_Inflation_Time", "Суммарное время раздутия DCB", "Число (сек)", "number"),
      f("Stent_Planned_Used", "Был ли ПЛАНОВЫЙ стент?", "1 = Да, 0 = Нет", "select"),
      f("Stent_Planned_Length", "Длина планового стента", "Число (мм)", "number"),
      f("BailOut_Stent_Used", "Потребовался BAIL-OUT стент?", "1 = Да, 0 = Нет", "select"),
      f("BailOut_Reason", "Причина спасательного стента", "0=Нет, 1=Дисс., 2=Рекойл, 3=Окклюзия, 99=N/A", "select"),
      f("BailOut_Stent_Length", "Длина bail-out стента", "Число (мм)", "number"),
      f("Stent_Length_Total", "Суммарная длина стентов", "Авто-расчет: planned + bail-out", "formula"),
    ],
  },
  {
    title: "Блок 5: интраоперационные осложнения",
    fields: [
      f("Complication_Any", "Факт любого осложнения", "1 = Да, 0 = Нет", "select"),
      f("Slow_No_Reflow", "Феномен Slow/No-reflow", "1 = Да, 0 = Нет", "select"),
      f("Distal_Embolization", "Дистальная эмболия русла", "1 = Да, 0 = Нет", "select"),
      f("Thrombosis_Acute", "Острый тромбоз", "1 = Да, 0 = Нет", "select"),
      f("Perforation", "Перфорация коронарной артерии", "0=Нет, 1=Тип I-II, 2=Тип III", "select"),
      f("Spasm_Severe", "Выраженный спазм артерии", "1 = Да, 0 = Нет", "select"),
      f("Complication_Therapy", "Доп. меры для купирования", "0=Нет, 1=Лек-ва, 2=IIb/IIIa, 3=Аспир., 4=Графт, 5=Перикард.", "select"),
    ],
  },
  {
    title: "Блок 6: результаты сразу после",
    fields: [
      f("MLD_Post", "Мин. диаметр просвета (QCA)", "Число (мм)", "number"),
      f("Residual_Stenosis", "Остаточный стеноз (QCA)", "Целое число %, без знака %", "number"),
      f("TIMI_Flow_Post", "Кровоток по TIMI в конце", "0, 1, 2, 3", "select"),
      f("IVUS_Post_Performed", "Выполнялось ли ВСУЗИ ПОСЛЕ?", "1 = Да, 0 = Нет", "select"),
      f("MLA_Post", "Мин. площадь просвета (ВСУЗИ)", "Число (мм2)", "number"),
      f("MLA_Post_Ratio", "Индекс расширения по ВСУЗИ", "Авто-расчет: MLA_Post / LA_Ref_Average", "formula"),
      f("Dissection_Final_Angio", "Финальная диссекция (Ангиография)", "0=Нет, 1=Тип A, 2=Тип B, 3=Тип C, 4=Тип D-F", "select"),
      f("Dissection_IVUS", "Финальная диссекция (ВСУЗИ)", "0=Нет, 1=Интима, 2=Медия, 3=Гематома, 99=N/A", "select"),
      f("Procedure_Success", "Успех (Стеноз < 30% + TIMI 3)", "1 = Да, 0 = Нет", "select"),
    ],
  },
  {
    title: "Блок 7: клиника в стационаре",
    fields: [
      f("ECG_Ischemia_Post", "Ишемия на ЭКГ после операции", "1 = Да, 0 = Нет", "select"),
      f("Angina_Post", "Рецидив стенокардии", "1 = Да, 0 = Нет", "select"),
      f("Unplanned_Angio", "Экстренная коронарография", "1 = Да, 0 = Нет", "select"),
      f("Unplanned_Finding", "What on angio?", "99=N/A, 0=Норма, 1=Тромбоз, 2=Дисс., 3=Рекойл", "select"),
      f("Repeat_PCI_InHosp", "Повторное ЧКВ до выписки", "1 = Да, 0 = Нет", "select"),
      f("MI_InHosp", "Инфаркт миокарда (в стационаре)", "1 = Да, 0 = Нет", "select"),
      f("Death_InHosp", "Смерть в стационаре", "1 = Да, 0 = Нет", "select"),
    ],
  },
  {
    title: "Блок 8: отдаленный контроль",
    fields: [
      f("FollowUp_Months", "Фактический срок контакта", "Число (мес)", "number"),
      f("FollowUp_Type", "Тип контроля", "0=Потерян, 1=Только клиника, 2=Клин+QCA", "select"),
      f("Symptom_Driven_Angio", "Повод для ангиографии", "99=N/A, 0=Плановая, 1=Симптомная", "select"),
      f("Ischemia_Test_Type_FU", "Тип нагрузочного теста", "0=Не проводился, 1=Стресс-ЭХОКГ, 2=ОФЭКТ, 3=ВЭМ, 4=ФРК", "select"),
      f("Ischemia_Value_FU", "Количественный результат нагрузочного теста", "Число (сегменты/%/ФРК)", "number"),
      f("Ischemia_Severe_FU", "Клинически значимая ишемия на follow-up", "1 = Да, 0 = Нет, 99 = N/A", "select"),
      f("MACE_FU", "События MACE (Смерть, ИМ, Инсульт)", "1 = Да, 0 = Нет", "select"),
      f("TLR_FU", "Повторная реваскуляризация", "1 = Да, 0 = Нет", "select"),
      f("MLD_FU", "Мин. диаметр просвета (QCA)", "Число (мм)", "number"),
      f("LLL_FU", "Поздняя потеря просвета (Late Loss)", "Авто-расчет: MLD_Post - MLD_FU", "formula"),
      f("Restenosis_Binary", "Бинарный рестеноз (> 50%)", "1 = Да, 0 = Нет", "select"),
    ],
  },
];

export const FIELD_MAP: Map<string, FieldDef> = new Map(
  SECTIONS.flatMap((section) => section.fields.map((field) => [field.id, field] as const)),
);

export const DEFAULT_COVARIATES: string[] = [
  "Age", "Sex", "BMI", "DM", "Hypertension", "Calcification_Angio", "Lesion_Length_Base",
];

/** Map strategy code to a readable label. */
export function strategyLabel(code: unknown): string {
  return (
    {
      "1": "DCB",
      "2": "ROTA+DCB",
      "3": "Hybrid",
      "4": "FMJ",
    }[String(code)] || String(code || "")
  );
}

export const DERIVED_FIELDS: FieldDef[] = [
  d("Immediate_Success_Composite", "Композитный непосредственный успех", "1 = Procedure_Success + нет осложнений + нет bail-out", "select", (row) => {
    if (!hasValue(row.Procedure_Success) || !hasValue(row.Complication_Any) || !hasValue(row.BailOut_Stent_Used)) return "";
    return row.Procedure_Success === "1" && row.Complication_Any === "0" && row.BailOut_Stent_Used === "0" ? "1" : "0";
  }),
  d("Positive_Remodeling_QCA", "Положительное ремоделирование QCA", "1 = MLD_FU > MLD_Post / LLL < 0", "select", (row) => {
    const post = num(row.MLD_Post);
    const follow = num(row.MLD_FU);
    if (!Number.isFinite(post) || !Number.isFinite(follow)) return "";
    return follow > post ? "1" : "0";
  }),
  d("Bailout_Conversion", "Конверсия безстентовой/гибридной стратегии в FMJ", "1 = план DCB/ROTA+DCB/гибрид + bail-out стент", "select", (row) => {
    if (!hasValue(row.Strategy_Planned) || !hasValue(row.BailOut_Stent_Used)) return "";
    return ["1", "2", "3"].includes(row.Strategy_Planned) && row.BailOut_Stent_Used === "1" ? "1" : "0";
  }),
  d("Strategy_Final_Simplified", "Фактическая стратегия с учетом bail-out", "DCB / ROTA+DCB / Hybrid / FMJ / Converted_FMJ", "select", (row) => {
    if (!hasValue(row.Strategy_Planned)) return "";
    if (["1", "2", "3"].includes(row.Strategy_Planned) && row.BailOut_Stent_Used === "1") return "Converted_FMJ";
    return strategyLabel(row.Strategy_Planned);
  }),
  d("No_Stent_Intention", "Изначально безстентовая/гибридная тактика", "1 = DCB, ROTA+DCB или гибрид", "select", (row) => {
    if (!hasValue(row.Strategy_Planned)) return "";
    return ["1", "2", "3"].includes(row.Strategy_Planned) ? "1" : "0";
  }),
];

export const DERIVED_MAP: Map<string, FieldDef> = new Map(DERIVED_FIELDS.map((field) => [field.id, field]));

export const ANALYSIS_COLUMNS: string[] = [...DB_COLUMNS, ...DERIVED_FIELDS.map((field) => field.id)];

export const ANALYSIS_SCENARIOS: Scenario[] = [
  {
    id: "manual",
    title: "Ручной выбор",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "MLA_Post_Ratio",
    covariates: DEFAULT_COVARIATES,
  },
  {
    id: "ni_immediate_fmj",
    title: "Non-inferiority: непосредственный успех vs FMJ",
    mode: "auto",
    group: "Strategy_Final_Simplified",
    outcome: "Immediate_Success_Composite",
    covariates: ["Age", "Sex", "DM", "CKD", "Calcification_Angio", "Lesion_Length_Base", "CTO_Present", "Bifurcation", "MLA_Base"],
    reference: "FMJ",
  },
  {
    id: "remodeling_qca",
    title: "Положительное ремоделирование QCA",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "Positive_Remodeling_QCA",
    covariates: ["Age", "Sex", "DM", "Calcification_Angio", "Lesion_Length_Base", "MLD_Post", "MLA_Base"],
    filter: (row) => ["1", "2", "3"].includes(row.Strategy_Planned),
    filterLabel: "только DCB, ROTA+DCB и гибрид",
    reference: "1",
  },
  {
    id: "late_loss",
    title: "Late lumen loss по стратегиям",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "LLL_FU",
    covariates: ["Age", "Sex", "DM", "Calcification_Angio", "Lesion_Length_Base", "MLD_Post"],
    reference: "4",
  },
  {
    id: "fu_ischemia_stress_test",
    title: "Отдаленная ишемия: нагрузочный тест",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "Ischemia_Severe_FU",
    covariates: ["Age", "Sex", "DM", "CKD", "Multivessel_CAD", "Calcification_Angio", "Lesion_Length_Base", "LVEF"],
    reference: "4",
  },
  {
    id: "mace_tlr",
    title: "Отдаленные события: MACE",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "MACE_FU",
    covariates: ["Age", "Sex", "DM", "CKD", "Multivessel_CAD", "Calcification_Angio", "Lesion_Length_Base"],
    reference: "4",
  },
  {
    id: "tlr",
    title: "Повторная реваскуляризация TLR",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "TLR_FU",
    covariates: ["Age", "Sex", "DM", "Calcification_Angio", "Lesion_Length_Base", "MLA_Post_Ratio"],
    reference: "4",
  },
  {
    id: "restenosis",
    title: "Бинарный рестеноз",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "Restenosis_Binary",
    covariates: ["Age", "Sex", "DM", "Calcification_Angio", "Lesion_Length_Base", "MLA_Post_Ratio"],
    reference: "4",
  },
  {
    id: "bailout_conversion",
    title: "Скрытая подгруппа: bail-out конверсия",
    mode: "auto",
    group: "Strategy_Planned",
    outcome: "Bailout_Conversion",
    covariates: ["Age", "Sex", "DM", "Calcification_Angio", "Calc_Arc_Max", "Calc_Length", "Lesion_Length_Base", "Bifurcation", "CTO_Present", "Dissection_Post_Prep"],
    filter: (row) => ["1", "2", "3"].includes(row.Strategy_Planned),
    filterLabel: "только изначально безстентовая/гибридная тактика",
  },
  {
    id: "rota_dcb_focus",
    title: "Фокус ROTA+DCB: успех и безопасность",
    mode: "auto",
    group: "Rota_Used",
    outcome: "Immediate_Success_Composite",
    covariates: ["Age", "Sex", "DM", "Calcification_Angio", "Calc_Arc_Max", "Calc_Length", "Lesion_Length_Base", "MLA_Base"],
  },
];

// --- Missing / Not-Applicable coding --------------------------------------
//
// A cell must never be silently empty when a procedure was simply not done.
// Convention (GCP/CDISC-style, agreed with the team):
//   - categorical (select) N/A  -> "99"
//   - quantitative (number/formula) N/A -> "-99" (biologically impossible, so
//     it can never collide with a real value; the analysis ignores it)
//   - free text N/A -> "N/A"
// A truly empty cell then means only "data should exist but is not entered yet".
// Sentinels are kept in exports (a monolithic dataset for SPSS/R) but MUST be
// treated as missing by every numeric consumer here — see sanitizeForAnalysis.
export const NA_CAT = "99";
export const NA_QUANT = "-99";

/** The N/A sentinel a field uses when its procedure was not performed. */
export function naCodeFor(meta: FieldDef): string {
  if (meta.type === "select") return NA_CAT;
  if (meta.type === "number" || meta.type === "formula") return NA_QUANT;
  return "N/A";
}

/** True when a stored value is an N/A sentinel for that field (= missing for analysis). */
export function isNaSentinel(id: string, value: string): boolean {
  const v = value.trim();
  if (v === "") return false;
  const meta = getFieldMeta(id);
  if (!meta) return false;
  if (meta.type === "select") return v === NA_CAT;
  if (meta.type === "number" || meta.type === "formula") return v === NA_QUANT || v === "-999";
  return v === "N/A";
}

/**
 * A gating rule: when `gate` holds one of `notDone`, the `dependents` are
 * auto-coded. Default target is the per-field N/A sentinel (categorical 99 /
 * quantitative -99), used when the procedure literally was not performed.
 * When `autoCode` is set, that value is written into every dependent instead —
 * this is the "cascade" flavor for facts that logically entail their
 * components: "no complications at all" -> every specific complication = 0.
 *
 * Deliberately excluded (different semantics, never auto-coded):
 *   - FollowUp_Type = 0 (lost) -> outcomes are MISSING (unknown), not N/A.
 *   - FollowUp_Type = 1 (clinical only) -> only QCA fields are N/A.
 */
export interface GatingRule {
  gate: string;
  notDone: string[];
  dependents: string[];
  /** Override for the auto-coded value. Omit for default N/A per field type. */
  autoCode?: string;
}

export const GATING_RULES: GatingRule[] = [
  { gate: "IVUS_Performed", notDone: ["0"], dependents: ["LA_Proximal", "LA_Distal", "MLA_Base", "Calc_Arc_Max", "Calc_Length", "Calc_Thickness", "Calc_Nodule", "IVUS_Calc_Mod_Confirmed"] },
  { gate: "IVUS_Post_Performed", notDone: ["0"], dependents: ["MLA_Post", "Dissection_IVUS"] },
  { gate: "CTO_Present", notDone: ["0"], dependents: ["CTO_Length", "J_CTO_Score", "CTO_Approach", "CTO_Tracking"] },
  { gate: "Bifurcation", notDone: ["0"], dependents: ["Medina_Class", "Bifurcation_Strategy"] },
  { gate: "Rota_Used", notDone: ["0"], dependents: ["Rota_Burr_Max"] },
  { gate: "HP_OPN_Used", notDone: ["0"], dependents: ["HP_OPN_Max_Press"] },
  { gate: "DCB_Used", notDone: ["0"], dependents: ["DCB_Type", "DCB_Diameter", "DCB_Length_Total", "DCB_Inflation_Time"] },
  { gate: "Stent_Planned_Used", notDone: ["0"], dependents: ["Stent_Planned_Length"] },
  { gate: "BailOut_Stent_Used", notDone: ["0"], dependents: ["BailOut_Reason", "BailOut_Stent_Length"] },
  { gate: "Ischemia_Test_Type", notDone: ["0"], dependents: ["Ischemia_Value", "Ischemia_Severe"] },
  { gate: "Ischemia_Test_Type_FU", notDone: ["0"], dependents: ["Ischemia_Value_FU", "Ischemia_Severe_FU"] },
  { gate: "Unplanned_Angio", notDone: ["0"], dependents: ["Unplanned_Finding"] },
  { gate: "Complication_Any", notDone: ["0"], autoCode: "0", dependents: ["Slow_No_Reflow", "Distal_Embolization", "Thrombosis_Acute", "Perforation", "Spasm_Severe", "Complication_Therapy"] },
];

/**
 * Return a shallow copy of a row with all N/A sentinels blanked, so the stats
 * module and derived computes see them as missing (never averaging a -99).
 * Display and export use the raw row and keep the sentinels intact.
 */
export function sanitizeForAnalysis(row: Row): Row {
  const out: Row = { ...row };
  for (const id of Object.keys(out)) {
    const value = out[id];
    if (typeof value === "string" && isNaSentinel(id, value)) out[id] = "";
  }
  return out;
}

/** Field metadata by id, including derived fields. */
export function getFieldMeta(id: string): FieldDef | undefined {
  return FIELD_MAP.get(id) || DERIVED_MAP.get(id);
}

/** Resolve a value for a row by id, computing derived fields on the fly. */
export function rowValue(row: Row, id: string): string {
  const derived = DERIVED_MAP.get(id);
  if (derived && derived.compute) return derived.compute(row);
  return row[id] ?? "";
}

/** Parse the choice options encoded in a select field's hint string. */
export function parseChoices(hint: string): { value: string; label: string }[] {
  if (!hint) return [];
  if (/^0,\s*1,\s*2,\s*3$/.test(hint.trim())) {
    return hint.split(",").map((v) => ({ value: v.trim(), label: v.trim() }));
  }
  const matches = [...hint.matchAll(/(?:(^)|[,;]\s*)(99|[0-9]+)\s*=\s*([^,;]+)/g)];
  return matches.map((match) => ({
    value: match[2],
    label: `${match[2]} — ${match[3].trim()}`,
  }));
}
